import { useEffect, useMemo, useState } from "react";
import {
    Alert, Box, Button, Card, CardContent, CircularProgress,
    Container, Divider, FormControl, FormControlLabel, Grid,
    IconButton, InputAdornment, InputLabel, MenuItem, Select,
    Stack, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon, Link as LinkIcon, LinkOff as LinkOffIcon, ArrowBack } from "@mui/icons-material";
import { useLocation, useNavigate, useParams } from "react-router";
import { routes } from "../routes";
import { ActionDto, ActionTypeDto, ConditionDto, IRuleDto } from "../api/generated";
import { useRule, useUpdateRule, useAddRule } from "../hooks/useRules";
import { useSettings } from "../hooks/useSettings";
import { useTranslation } from "react-i18next";

const deepClone = <T,>(obj: T): T => JSON.parse(JSON.stringify(obj));

const emptyRule = (): IRuleDto => ({
    id: "",
    name: "",
    description: "",
    priority: 0,
    enabled: true,
    // @ts-ignore
    conditions: [],
    // @ts-ignore
    actions: [],
});

const CONDITION_TYPES = ["Extension", "NameContains", "MimeType", "Regex"];
const ACTION_TYPES = ["Move", "Copy", "Delete", "Rename"];
const MAX_ACTIONS = 1;

const COMMON_EXTENSIONS = [
    ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx",
    ".txt", ".csv", ".jpg", ".jpeg", ".png", ".gif", ".bmp", ".svg",
    ".zip", ".rar", ".7z", ".mp3", ".wav", ".mp4", ".avi", ".mkv",
    ".exe", ".msi", ".iso", ".html", ".json", ".xml",
];

const INVALID_CHARS_REGEX = /[<>:"|?*]/;
const DRIVE_PATH_REGEX = /^[a-zA-Z]:\\/;
const UNC_PATH_REGEX = /^\\\\[^\\/]+\\[^\\/]+/;

function requiresPath(actionType?: string) {
    return actionType === "Move" || actionType === "Copy";
}

export default function RuleFormPage() {
    const { id } = useParams<{ id?: string }>();
    const isNew = !id;
    const navigate = useNavigate();
    const location = useLocation() as any;
    const { t } = useTranslation();

    function getWindowsPathError(value: string): string | null {
        const path = (value ?? "").trim();
        if (!path) return t('ruleForm.validation.required');
        const isDrive = DRIVE_PATH_REGEX.test(path);
        const isUnc = UNC_PATH_REGEX.test(path);
        if (!isDrive && !isUnc) return t('ruleForm.validation.badStart');
        const toCheck = isDrive ? path.replace(/^[a-zA-Z]:/, "") : path;
        if (INVALID_CHARS_REGEX.test(toCheck)) return t('ruleForm.validation.invalidChars');
        const segments = isDrive
            ? path.split("\\").slice(1)
            : path.replace(/^\\\\/, "").split("\\").slice(2);
        for (const seg of segments) {
            if (!seg) continue;
            if (seg.endsWith(".") || seg.endsWith(" ")) return t('ruleForm.validation.badSegment');
        }
        return null;
    }

    const stateRule: IRuleDto | undefined = location?.state?.rule;
    const effectiveId = stateRule?.id ?? id;
    const { data: fetchedRule, isLoading, isError, error } = useRule(isNew ? undefined : (effectiveId as string));
    const sourceRule = useMemo<IRuleDto | undefined>(
        () => (isNew ? undefined : (stateRule ?? fetchedRule)),
        [isNew, stateRule, fetchedRule]
    );

    const [form, setForm] = useState<IRuleDto>(emptyRule());
    const updateMutation = useUpdateRule();
    const createMutation = useAddRule();
    const { data: settings } = useSettings();
    const baseWatchFolder = (settings?.watchFolder || "").trim();
    const baseWatchFolderLower = baseWatchFolder.toLowerCase();

    useEffect(() => {
        if (isNew) setForm(emptyRule());
        else if (sourceRule) setForm(deepClone(sourceRule));
    }, [isNew, sourceRule, sourceRule?.id]);

    useEffect(() => {
        setForm(prev => {
            if ((prev.actions?.length ?? 0) > MAX_ACTIONS)
                return { ...prev, actions: prev.actions?.slice(0, MAX_ACTIONS) };
            return prev;
        });
    }, []);

    const handleText = (key: keyof IRuleDto) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(prev => ({ ...prev, [key]: e.target.value as any }));

    const handleNumber = (key: keyof IRuleDto) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(prev => ({ ...prev, [key]: Number(e.target.value) as any }));

    const handleBool = (key: keyof IRuleDto) => (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
        setForm(prev => ({ ...prev, [key]: checked as any }));

    const addCondition = () =>
        setForm(prev => ({
            ...prev,
            conditions: [...(prev.conditions ?? []), { type: CONDITION_TYPES[0], value: "" } as ConditionDto],
        }));

    const updateCondition = (index: number, patch: Partial<ConditionDto>) =>
        setForm(prev => {
            const next = [...(prev.conditions ?? [])];
            next[index] = { ...(next[index] ?? {}), ...patch } as ConditionDto;
            return { ...prev, conditions: next };
        });

    const removeCondition = (index: number) =>
        setForm(prev => {
            const next = [...(prev.conditions ?? [])];
            next.splice(index, 1);
            return { ...prev, conditions: next };
        });

    const addAction = () =>
        setForm(prev => {
            if ((prev.actions?.length ?? 0) >= MAX_ACTIONS) return prev;
            return { ...prev, actions: [...(prev.actions ?? []), { type: ACTION_TYPES[0], target: "" } as ActionDto] };
        });

    const updateAction = (index: number, patch: Partial<ActionDto>) =>
        setForm(prev => {
            const next = [...(prev.actions ?? [])];
            next[index] = { ...(next[index] ?? {}), ...patch } as ActionDto;
            return { ...prev, actions: next };
        });

    const removeAction = (index: number) =>
        setForm(prev => {
            const next = [...(prev.actions ?? [])];
            next.splice(index, 1);
            return { ...prev, actions: next };
        });

    function isActionRelative(target?: string) {
        if (!baseWatchFolder || !target) return false;
        return target.toLowerCase().startsWith(baseWatchFolderLower + "\\") || target.toLowerCase() === baseWatchFolderLower;
    }

    function getSubfolderFromTarget(target?: string) {
        if (!isActionRelative(target) || !target) return "";
        if (target.toLowerCase() === baseWatchFolderLower) return "";
        return target.slice(baseWatchFolder.length + 1);
    }

    function joinBaseAndSub(sub: string) {
        if (!baseWatchFolder) return sub;
        const cleaned = sub.replace(/^\\+/, "");
        if (!cleaned) return baseWatchFolder;
        return baseWatchFolder.replace(/\\+$/, "") + "\\" + cleaned;
    }

    const anyInvalidActionPath = (form.actions ?? []).some(
        a => requiresPath(a?.type) && !!getWindowsPathError(a?.target ?? "")
    );
    const canSave =
        (form.name ?? "").trim().length > 0 &&
        !anyInvalidActionPath &&
        !(updateMutation.isPending || createMutation.isPending);

    const onBack = () => navigate(routes.rules.to());
    const onSave = () => {
        const payload = deepClone(form);
        if (isNew) {
            if (!payload.id) delete (payload as any).id;
            createMutation.mutate(payload, { onSuccess: () => navigate(routes.rules.to()) });
        } else {
            updateMutation.mutate(payload, { onSuccess: () => navigate(routes.rules.to()) });
        }
    };

    if (!isNew && isLoading && !sourceRule) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!isNew && isError && !sourceRule) {
        return (
            <Container maxWidth="md" sx={{ pt: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {(error as any)?.message ?? t('ruleForm.loadError')}
                </Alert>
                <Button startIcon={<ArrowBack />} onClick={onBack}>{t('ruleForm.back')}</Button>
            </Container>
        );
    }

    const conditionTypeLabels: Record<string, string> = {
        Extension: t('ruleForm.conditionTypes.Extension'),
        NameContains: t('ruleForm.conditionTypes.NameContains'),
        MimeType: t('ruleForm.conditionTypes.MimeType'),
        Regex: t('ruleForm.conditionTypes.Regex'),
    };

    const actionTypeLabels: Record<string, string> = {
        Move: t('ruleForm.actionTypes.Move'),
        Copy: t('ruleForm.actionTypes.Copy'),
        Delete: t('ruleForm.actionTypes.Delete'),
        Rename: t('ruleForm.actionTypes.Rename'),
    };

    return (
        <Box sx={{ height: '100%', bgcolor: '#F8FAFC', overflowY: 'auto' }}>
            <Container maxWidth="lg" sx={{ pt: 2 }}>
                <Stack direction="row" justifyContent="flex-end" spacing={1.5} mb={2}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={onBack}
                    >
                        {isNew ? t('ruleForm.cancel') : t('ruleForm.back')}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!canSave}
                        onClick={onSave}
                        sx={{ fontWeight: 700 }}
                    >
                        {updateMutation.isPending || createMutation.isPending
                            ? t('ruleForm.saving')
                            : isNew ? t('ruleForm.create') : t('ruleForm.saveChanges')}
                    </Button>
                </Stack>
                <Grid container spacing={3} columns={12}>
                    {/* Columna principal */}
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Stack spacing={3}>

                            {/* Información básica */}
                            <Card sx={{ border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" fontWeight={600} sx={{ color: '#0F172A', mb: 0.5 }}>
                                        {t('ruleForm.basicInfo')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#64748B', mb: 2.5 }}>
                                        {t('ruleForm.basicInfoDesc')}
                                    </Typography>
                                    <Divider sx={{ mb: 3, borderColor: '#F1F5F9' }} />
                                    <Stack spacing={2.5}>
                                        <TextField
                                            label={t('ruleForm.name')}
                                            value={form.name ?? ""}
                                            onChange={handleText("name")}
                                            autoFocus
                                            fullWidth
                                            placeholder={t('ruleForm.namePlaceholder')}
                                        />
                                        <TextField
                                            label={t('ruleForm.description')}
                                            value={form.description ?? ""}
                                            onChange={handleText("description")}
                                            multiline
                                            minRows={2}
                                            fullWidth
                                            placeholder={t('ruleForm.descriptionPlaceholder')}
                                        />
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <TextField
                                                label={t('ruleForm.priority')}
                                                type="number"
                                                value={Number(form.priority ?? 0)}
                                                onChange={handleNumber("priority")}
                                                slotProps={{ htmlInput: { min: 0 } }}
                                                sx={{ width: 160 }}
                                                helperText={t('ruleForm.priorityHelper')}
                                            />
                                            <Box sx={{ flexGrow: 1 }} />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={Boolean(form.enabled)}
                                                        onChange={handleBool("enabled")}
                                                        color="success"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" fontWeight={500} sx={{ color: form.enabled ? '#10B981' : '#94A3B8' }}>
                                                        {form.enabled ? t('ruleForm.ruleActive') : t('ruleForm.ruleInactive')}
                                                    </Typography>
                                                }
                                            />
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>

                            {/* Condiciones */}
                            <Card sx={{ border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                                        <Box>
                                            <Typography variant="h6" fontWeight={600} sx={{ color: '#0F172A' }}>
                                                {t('ruleForm.conditions')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
                                                {t('ruleForm.conditionsDesc')}
                                            </Typography>
                                        </Box>
                                        <Button
                                            startIcon={<AddIcon />}
                                            onClick={addCondition}
                                            variant="outlined"
                                            size="small"
                                            sx={{ flexShrink: 0, borderColor: '#E2E8F0', color: '#2563EB', '&:hover': { borderColor: '#2563EB', bgcolor: 'rgba(37,99,235,0.04)' } }}
                                        >
                                            {t('ruleForm.addCondition')}
                                        </Button>
                                    </Stack>
                                    <Divider sx={{ my: 2.5, borderColor: '#F1F5F9' }} />

                                    {(form.conditions ?? []).length === 0 ? (
                                        <Box sx={{ py: 3, textAlign: 'center', bgcolor: '#F8FAFC', borderRadius: 1.5, border: '1px dashed #E2E8F0' }}>
                                            <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                                                {t('ruleForm.noConditions')}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Stack spacing={1.5}>
                                            {(form.conditions ?? []).map((c, idx) => (
                                                <Box
                                                    key={idx}
                                                    sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 2, bgcolor: '#F8FAFC', borderRadius: 1.5, border: '1px solid #F1F5F9' }}
                                                >
                                                    <FormControl sx={{ minWidth: 180 }}>
                                                        <InputLabel id={`cond-type-${idx}`}>{t('ruleForm.conditionType')}</InputLabel>
                                                        <Select
                                                            labelId={`cond-type-${idx}`}
                                                            label={t('ruleForm.conditionType')}
                                                            value={c?.type ?? ""}
                                                            onChange={(e) => updateCondition(idx, { type: e.target.value as any, value: "" })}
                                                            size="small"
                                                        >
                                                            {CONDITION_TYPES.map(tp => (
                                                                <MenuItem key={tp} value={tp}>{conditionTypeLabels[tp] ?? tp}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>

                                                    {c?.type === "Extension" ? (
                                                        <FormControl sx={{ flex: 1 }}>
                                                            <InputLabel id={`cond-val-${idx}`}>{t('ruleForm.conditionExtension')}</InputLabel>
                                                            <Select
                                                                labelId={`cond-val-${idx}`}
                                                                label={t('ruleForm.conditionExtension')}
                                                                value={c?.value ?? ""}
                                                                onChange={(e) => updateCondition(idx, { value: e.target.value })}
                                                                size="small"
                                                            >
                                                                {c?.value && !COMMON_EXTENSIONS.includes(c.value) && (
                                                                    <MenuItem value={c.value}>{c.value}</MenuItem>
                                                                )}
                                                                {COMMON_EXTENSIONS.map(ext => (
                                                                    <MenuItem key={ext} value={ext}>{ext}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    ) : (
                                                        <TextField
                                                            label={t('ruleForm.conditionValue')}
                                                            value={c?.value ?? ""}
                                                            onChange={(e) => updateCondition(idx, { value: e.target.value })}
                                                            size="small"
                                                            fullWidth
                                                            sx={{ flex: 1 }}
                                                        />
                                                    )}

                                                    <Tooltip title={t('ruleForm.deleteCondition')}>
                                                        <IconButton size="small" onClick={() => removeCondition(idx)} sx={{ color: '#EF4444', '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' }, mt: 0.25 }}>
                                                            <DeleteIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            ))}
                                        </Stack>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Acción */}
                            <Card sx={{ border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                                        <Box>
                                            <Typography variant="h6" fontWeight={600} sx={{ color: '#0F172A' }}>
                                                {t('ruleForm.action')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
                                                {t('ruleForm.actionDesc')}
                                            </Typography>
                                        </Box>
                                        {(form.actions?.length ?? 0) < MAX_ACTIONS && (
                                            <Button
                                                startIcon={<AddIcon />}
                                                onClick={addAction}
                                                variant="outlined"
                                                size="small"
                                                sx={{ flexShrink: 0, borderColor: '#E2E8F0', color: '#2563EB', '&:hover': { borderColor: '#2563EB', bgcolor: 'rgba(37,99,235,0.04)' } }}
                                            >
                                                {t('ruleForm.addAction')}
                                            </Button>
                                        )}
                                    </Stack>
                                    <Divider sx={{ my: 2.5, borderColor: '#F1F5F9' }} />

                                    {(form.actions ?? []).length === 0 ? (
                                        <Box sx={{ py: 3, textAlign: 'center', bgcolor: '#F8FAFC', borderRadius: 1.5, border: '1px dashed #E2E8F0' }}>
                                            <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                                                {t('ruleForm.noAction')}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Stack spacing={1.5}>
                                            {(form.actions ?? []).map((a, idx) => {
                                                const showPath = requiresPath(a?.type);
                                                const relativeCapable = showPath && !!baseWatchFolder;
                                                const relative = relativeCapable && isActionRelative(a?.target);
                                                const subfolder = relative ? getSubfolderFromTarget(a?.target) : "";
                                                const fullForValidation = relative ? joinBaseAndSub(subfolder) : (a?.target ?? "");
                                                const errorMsg = showPath ? getWindowsPathError(fullForValidation) : null;

                                                return (
                                                    <Box
                                                        key={idx}
                                                        sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 2, bgcolor: '#F8FAFC', borderRadius: 1.5, border: '1px solid #F1F5F9' }}
                                                    >
                                                        <FormControl sx={{ minWidth: 160 }}>
                                                            <InputLabel id={`act-type-${idx}`}>{t('ruleForm.actionType')}</InputLabel>
                                                            <Select
                                                                labelId={`act-type-${idx}`}
                                                                label={t('ruleForm.actionType')}
                                                                value={a?.type ?? ""}
                                                                onChange={(e) => updateAction(idx, { type: e.target.value as any })}
                                                                size="small"
                                                            >
                                                                {ACTION_TYPES.map(tp => (
                                                                    <MenuItem key={tp} value={tp}>{actionTypeLabels[tp] ?? tp}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>

                                                        {showPath && (
                                                            relative ? (
                                                                <TextField
                                                                    label={t('ruleForm.subfolder')}
                                                                    value={subfolder}
                                                                    onChange={(e) => updateAction(idx, { target: joinBaseAndSub(e.target.value) })}
                                                                    error={!!errorMsg}
                                                                    helperText={errorMsg ?? t('ruleForm.fullPath', { path: fullForValidation })}
                                                                    size="small"
                                                                    fullWidth
                                                                    sx={{ flex: 1 }}
                                                                    slotProps={{
                                                                        input: {
                                                                            startAdornment: (
                                                                                <InputAdornment position="start" sx={{ fontSize: 11 }}>
                                                                                    {baseWatchFolder}\
                                                                                </InputAdornment>
                                                                            ),
                                                                        },
                                                                    }}
                                                                />
                                                            ) : (
                                                                <TextField
                                                                    disabled={a?.type === ActionTypeDto.Delete}
                                                                    label={t('ruleForm.targetPath')}
                                                                    value={a?.target ?? ""}
                                                                    onChange={(e) => updateAction(idx, { target: e.target.value })}
                                                                    error={!!errorMsg}
                                                                    helperText={
                                                                        errorMsg ?? (relativeCapable
                                                                            ? t('ruleForm.relativeModeHint')
                                                                            : " ")
                                                                    }
                                                                    size="small"
                                                                    fullWidth
                                                                    sx={{ flex: 1 }}
                                                                    placeholder={t('ruleForm.targetPathPlaceholder')}
                                                                />
                                                            )
                                                        )}

                                                        {showPath && (
                                                            <Tooltip title={
                                                                !relativeCapable
                                                                    ? t('ruleForm.relativeNoWatch')
                                                                    : relative ? t('ruleForm.disableRelative') : t('ruleForm.enableRelative')
                                                            } arrow>
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        disabled={!relativeCapable}
                                                                        onClick={() => {
                                                                            if (relative) {
                                                                                if (a?.target?.toLowerCase() === baseWatchFolderLower)
                                                                                    updateAction(idx, { target: "" });
                                                                            } else {
                                                                                const current = a?.target ?? "";
                                                                                if (!current.toLowerCase().startsWith(baseWatchFolderLower + "\\"))
                                                                                    updateAction(idx, { target: baseWatchFolder });
                                                                            }
                                                                        }}
                                                                        sx={{ color: relative ? '#2563EB' : '#94A3B8', mt: 0.25 }}
                                                                    >
                                                                        {relative ? <LinkIcon sx={{ fontSize: 18 }} /> : <LinkOffIcon sx={{ fontSize: 18 }} />}
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        )}

                                                        <Tooltip title={t('ruleForm.deleteAction')}>
                                                            <IconButton size="small" onClick={() => removeAction(idx)} sx={{ color: '#EF4444', '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' }, mt: 0.25 }}>
                                                                <DeleteIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    )}
                                </CardContent>
                            </Card>

                        </Stack>
                    </Grid>

                    {/* Panel lateral */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ border: '1px solid #E2E8F0', boxShadow: 'none', position: 'sticky', top: 16 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" fontWeight={600} sx={{ color: '#0F172A', mb: 2 }}>
                                    {t('ruleForm.howItWorks')}
                                </Typography>
                                <Stack spacing={2}>
                                    {[
                                        { n: "1", title: t('ruleForm.step1Title'), desc: t('ruleForm.step1Desc') },
                                        { n: "2", title: t('ruleForm.step2Title'), desc: t('ruleForm.step2Desc') },
                                        { n: "3", title: t('ruleForm.step3Title'), desc: t('ruleForm.step3Desc') },
                                    ].map(({ n, title, desc }) => (
                                        <Box key={n} sx={{ display: 'flex', gap: 1.5 }}>
                                            <Box sx={{ bgcolor: 'rgba(37,99,235,0.1)', color: '#2563EB', width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem', flexShrink: 0, mt: 0.1 }}>
                                                {n}
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600} sx={{ color: '#1E293B' }}>{title}</Typography>
                                                <Typography variant="caption" sx={{ color: '#64748B', lineHeight: 1.6, display: 'block', mt: 0.25 }}>{desc}</Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                                <Divider sx={{ my: 2.5, borderColor: '#F1F5F9' }} />
                                <Typography variant="caption" sx={{ color: '#94A3B8', lineHeight: 1.6, display: 'block' }}>
                                    {t('ruleForm.footerNote')}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
            <Box sx={{ pb: 6 }} />
        </Box>
    );
}
